import React from 'react';
import { triggerDownload } from './downloadFile';
import { ensureImportMetaRegistry } from './pdfRuntime.web';
import { prefetchImages } from './pdfImages.web';
import { buildFundingMap, FUNDING_MAP_SIZE, type FundingMapSpec } from './fundingRequestMap';
import { fundingRequestFilename, type FundingRequestModel } from './fundingRequest';
import type { DefensePlan } from '../types';

export const isPdfDownloadSupported = true;

/**
 * Build the funding request as a PDF and hand it to the browser as a
 * download. `@react-pdf/renderer` and the document are imported lazily, as
 * in reportPdf.web.ts, so the Funding screen can import this statically
 * without growing the entry bundle.
 */
export async function downloadFundingRequestPdf(
  model: FundingRequestModel,
  plan: DefensePlan
): Promise<void> {
  ensureImportMetaRegistry();

  const [{ pdf }, { FundingRequestDocument }] = await Promise.all([
    import('@react-pdf/renderer'),
    import('../components/report/FundingRequestDocument.web'),
  ]);

  // The map is a nicety: a bad coordinate or a blocked tile server must never
  // stop the download. No tiles means the steps draw on a plain boxed page.
  let map: FundingMapSpec | null = null;
  let images = new Map<string, string>();
  try {
    map = buildFundingMap(model.mapFeatures, FUNDING_MAP_SIZE);
    if (map) images = (await prefetchImages(map.tiles.map((tile) => tile.url))).map;
  } catch {
    map = null;
  }

  const doc = React.createElement(FundingRequestDocument, { model, map, images });
  // Same boundary cast as reportPdf.web.ts: the component's props are the
  // model, not <Document>'s, though the rendered tree is a valid <Document>.
  const blob = await pdf(doc as Parameters<typeof pdf>[0]).toBlob();
  const hasSources = model.sources.length > 0 || model.otherSource.length > 0;
  triggerDownload(blob, fundingRequestFilename(plan, new Date(), hasSources));
}
